/**
 * Geosoft daily QC, batch 5 part B (docs/specs/geosoft-qc.md §18 to §21): the rules behind the flight database Teslyn
 * makes when there is none (MB's F10, decisions 504 and 506), the plots made by themselves after a run (F12), the
 * flight path checked against each block's own lines (N14) and "Set the zone" (decision 847). Teslyn Field's core
 * lists files and runs Oasis montaj; what to run is decided here.
 */
import type { Block } from './model.js';
import type { QcFamily, QcLineResult, QcRun } from './geosoft-qc.js';
import { qcReviewPlan, qcReviewViewId, QC_SCRIPT_FOLDERS, type QcReviewIssue } from './qc-review-view.js';
import { utmZoneName } from './geosoft-projection.js';
import { qcScriptHeights, type QcScriptHeight } from './qc-height-sources.js';

// ------------------------------------------------------------------------------------- F10: the flight database

/**
 * Script 1 of each aircraft type's set (decision 504): the one that derives the `_ED`, `_D1`, `_D4` and `_HP101`
 * channels from the imported XYZ, as the crew runs it first (`test.gs`, 15 Oct 2026). Not the "1A" first-view script.
 */
const PROCESSING: Readonly<Record<QcFamily, readonly RegExp[]>> = {
  c208: [/^1\.\s*Copy_Interp_Scale_Filter_Channels_Gradient\.gs$/i, /^1\.\s*Copy_Interp.*\.gs$/i],
  c210: [/^C210_Script_1_V[\d.]+_No_ADU\.gs$/i, /^C210_Script_1_V[\d.]+(?!.*First_View).*\.gs$/i],
  heli: [/^Heli_Script_1_V[\d.]+\.gs$/i],
};

/** The aircraft type's script 1 among the names of its folder, or null when the folder has none. */
export function qcProcessingScript(family: QcFamily, names: readonly string[]): string | null {
  const sorted = [...names].filter((n) => /\.gs$/i.test(n)).sort((a, b) => a.localeCompare(b));
  for (const re of PROCESSING[family]) {
    const hit = sorted.find((n) => re.test(n));
    if (hit) return hit;
  }
  return null;
}

/** What Teslyn Field's core found for a flight without a database (`geosoft_flight_db_plan`). */
export interface FlightDbFacts {
  /** The flight's survey-line XYZ files (`009053712.XYZ`), in import order. */
  readonly xyz: readonly string[];
  /** Where the database would go (`…\Geosoft\Database\Flt09.gdb`), null when no Geosoft folder can hold it. */
  readonly target: string | null;
  /** The project's script folders and their scripts (`Geosoft\Scripts`), after the template was copied in. */
  readonly folders: readonly { readonly name: string; readonly scripts: readonly string[] }[];
}

/** What to ask the core to make, or why it cannot. */
export type FlightDbPlan =
  | {
      readonly ok: true;
      /** The aircraft type's folder and its script 1; null runs only the import (and says so). */
      readonly folder: string;
      readonly script: string | null;
      readonly target: string;
      readonly xyz: readonly string[];
    }
  | { readonly ok: false; readonly reason: string };

/** F10: the flight database from the flight's XYZ with the aircraft type's script 1. */
export function flightDbPlan(family: QcFamily, facts: FlightDbFacts, label: string): FlightDbPlan {
  if (facts.xyz.length === 0)
    return {
      ok: false,
      reason: `${label} has no Geosoft database and its folder has no survey-line XYZ file (<NNN><hhmmss>.XYZ) to make one from.`,
    };
  if (!facts.target)
    return {
      ok: false,
      reason: `${label}: the project has no Geosoft folder to make the flight database in.`,
    };
  const folder = QC_SCRIPT_FOLDERS[family];
  const own = facts.folders.find((f) => f.name.toLowerCase() === folder.toLowerCase());
  return {
    ok: true,
    folder: own?.name ?? folder,
    script: own ? qcProcessingScript(family, own.scripts) : null,
    target: facts.target,
    xyz: facts.xyz,
  };
}

// ------------------------------------------------------------------------------- N14: each block's own lines

/** A block's own planned-lines database, `Lines_<block key>.gdb` (the H26036 pattern) or, per block, `Lines.gdb`. */
export interface BlockLinesGdb {
  readonly key: string;
  readonly path: string;
  /**
   * The planned line names in it (`L100010`, `T190050`), read natively by Teslyn Field's core; absent or null when
   * they could not be read (the block digit then decides, as before).
   */
  readonly lines?: readonly string[] | null;
}

/** The block a Geosoft line name belongs to: its first digit after the letter (`L301780` → block digit 3). */
const digitOf = (lineName: string): string => lineName.replace(/^[A-Z]+/, '').charAt(0);

/** The number of a Geosoft line name (`L100431` → 100431, `T190050:2` → 190050), NaN when it has none. */
const numberOf = (lineName: string): number => Number(lineName.replace(/^[A-Z]+/, '').split(':')[0]);

/**
 * The planned line a flown line is, or repeats: a reflight or patch keeps the number of the line it reflies with its
 * last digit raised (decision 879: `L100431` reflies `L100430`), so a block's plan "holds" a flown line when it has
 * the number itself or the number with the last digit 0. Numbers only: the letter (`L`, `T`) and a `:version` are
 * left out, as a plan file writes a tie as `Line 190050` while the block's `Lines.gdb` names it `T190050`.
 */
export function plannedLineHolds(planned: readonly string[], lineName: string): boolean {
  const n = numberOf(lineName);
  if (!Number.isFinite(n)) return false;
  const original = n - (n % 10);
  return planned.some((p) => {
    const q = numberOf(p);
    return q === n || q === original;
  });
}

/** One flight path check (`GeosoftCheck` `path` of the run spec). */
export interface FlightPathCheck {
  readonly gx: 'path';
  readonly lines_gdb: string;
  readonly alldev_m: number;
  readonly dist_m: number;
  readonly lines?: readonly string[];
  readonly block?: string;
}

/**
 * The flight path checks of a flight (N14): where blocks have their own `Lines_<block>.gdb` (or, laid out per block,
 * `Lines.gdb`), each flown block's lines against its own database; the lines of a block without one against the
 * project's `Lines.gdb` (all of them when no block has its own, as on F26048). `skipped` names the blocks that have
 * neither.
 *
 * Which block a line is checked against (MB, 3 Nov 2026: F26044's Flt 004, flown in the Northern block, also got a
 * check and an XTQC map against Central East's lines): where the blocks' planned line names are known (`lines` of
 * `blockLinesGdbs`, read from each block's own database), the block whose plan holds the line or the line it
 * repeats (`plannedLineHolds`), the block of its first digit when two do; a line in no block's plan (a transit, or a
 * name read from the database that is no line) is checked against none and named in `skipped`, so it shows under the
 * run. The first digit alone decides only where no block's names could be read (the H26036 pattern, or a database
 * that cannot be opened). Test, comp-box and calibration lines (numbers below 10000, Q783) are left out quietly.
 */
export function qcFlightPathChecks(input: {
  readonly linesGdb: string | null;
  readonly blockLinesGdbs?: readonly BlockLinesGdb[] | null;
  readonly blocks: readonly Pick<Block, 'digit' | 'name' | 'kind' | 'key'>[];
  readonly lineNames: readonly string[];
  readonly alldevM: number;
  readonly distM: number;
}): { readonly checks: FlightPathCheck[]; readonly skipped: string[] } {
  const own = input.blockLinesGdbs ?? [];
  const base = { gx: 'path' as const, alldev_m: input.alldevM, dist_m: input.distM };
  if (own.length === 0)
    return input.linesGdb
      ? { checks: [{ ...base, lines_gdb: input.linesGdb }], skipped: [] }
      : { checks: [], skipped: ['Flight path: no planned lines database (Lines.gdb) was found.'] };
  const checks: FlightPathCheck[] = [];
  const skipped: string[] = [];
  const rest: string[] = [];
  const unplanned: string[] = [];
  const surveyed = input.blocks.filter((b) => b.kind === 'survey');
  const byDigit = new Map(surveyed.map((b) => [b.digit, b]));
  const dbOf = (b: Pick<Block, 'key'>): BlockLinesGdb | undefined =>
    own.find((o) => o.key.toLowerCase() === b.key.toLowerCase());
  const isTest = (n: string): boolean => {
    const v = numberOf(n);
    return Number.isFinite(v) && v < 10_000;
  };
  // the blocks whose planned names are known: with none, the first digit decides as before
  const known = surveyed.filter((b) => {
    const db = dbOf(b);
    return db?.lines != null && db.lines.length > 0;
  });
  const blockOf = (n: string): Pick<Block, 'digit' | 'name' | 'kind' | 'key'> | null => {
    const byFirstDigit = byDigit.get(digitOf(n)) ?? null;
    if (known.length === 0) return byFirstDigit;
    const holders = known.filter((b) => plannedLineHolds(dbOf(b)!.lines!, n));
    if (holders.length > 0) return holders.find((b) => b === byFirstDigit) ?? holders[0]!;
    // its digit's block has no names to go by (no database of its own, or one that could not be read): the digit
    // decides for that block only, and the line is checked or named as before
    if (byFirstDigit && !known.includes(byFirstDigit)) return byFirstDigit;
    return null;
  };
  const groups = new Map<string, string[]>();
  for (const n of input.lineNames) {
    if (isTest(n)) continue;
    const b = blockOf(n);
    const db = b ? dbOf(b) : undefined;
    if (b && db) groups.set(b.key, [...(groups.get(b.key) ?? []), n]);
    else if (b) rest.push(n);
    else if (known.length > 0) unplanned.push(n);
  }
  for (const b of surveyed) {
    const lines = groups.get(b.key);
    const db = dbOf(b);
    if (lines && db) checks.push({ ...base, lines_gdb: db.path, lines, block: b.key });
  }
  // the lines of a block without its own database: against the project's Lines.gdb, else named
  if (rest.length > 0) {
    if (input.linesGdb) checks.push({ ...base, lines_gdb: input.linesGdb, lines: rest });
    else {
      const names = [...new Set(rest.map((n) => byDigit.get(digitOf(n))!.name))];
      skipped.push(`Flight path of ${names.join(', ')}: no Lines_<block>.gdb or Lines.gdb was found.`);
    }
  }
  if (unplanned.length > 0)
    skipped.push(
      `Flight path of ${unplanned.join(', ')}: not checked, in no block's planned lines (Lines.gdb).`,
    );
  return { checks, skipped };
}

// ------------------------------------------------------------------------ F12: the plots after a QC run

/** One view of the plots made after a run: the view script and the lines it is made for. */
export interface QcAfterRunView {
  /** Decision 879: the run's database that holds the lines (one of its blocks'); absent: the run's own. */
  readonly gdb?: string;
  readonly folder: string;
  readonly script: string;
  /** `folder/script`, the slot the plots are kept under (`qcReviewViewId`). */
  readonly view: string;
  readonly lines: readonly {
    readonly result_id: string;
    readonly line_name: string;
    /** The issue the plots are kept for (the view's first, as "Review line" keeps them). */
    readonly issue: QcReviewIssue;
    /** MB's F11: the project's heights the heights script is given for this line (`qcScriptHeights`). */
    readonly heights: readonly QcScriptHeight[];
  }[];
}

/** What gives the heights script the project's heights (F11), as "Review line" uses it. */
export interface QcAfterRunHeights {
  readonly blocks: readonly Pick<Block, 'digit' | 'kind' | 'terrain_clearance_m' | 'key'>[];
  readonly hardDeckM: number | null;
  readonly lowerWorkingM: number | null;
}

/**
 * F12 (MB, 24 Oct 2026): after a QC run, the plots of every line that failed or needs a check are made by themselves,
 * so "Review line" shows them at once (decision 730: it shows kept plots without starting anything). Each breach's
 * view (R117: `qcReviewPlan`), the lines of one view together, in the run's line order. Breaches with no script in
 * the folder are left out (Review line says so when pressed).
 */
export function qcAfterRunViews(
  run: Pick<QcRun, 'family'>,
  lines: readonly QcLineResult[],
  folder: string,
  scripts: readonly string[],
  heights: QcAfterRunHeights | null = null,
): QcAfterRunView[] {
  const views = new Map<string, QcAfterRunView & { lines: QcAfterRunView['lines'][number][] }>();
  for (const l of lines) {
    if (l.verdict !== 'fail' && l.verdict !== 'warn') continue;
    for (const v of qcReviewPlan(l, run.family, scripts)) {
      if (!v.script) continue;
      const id = qcReviewViewId(folder, v.script);
      const at = views.get(id) ?? { folder, script: v.script, view: id, lines: [] };
      if (!at.lines.some((x) => x.result_id === l.id))
        at.lines.push({
          result_id: l.id,
          line_name: l.line_name,
          issue: v.issues[0]!,
          // the same heights "Review line" gives the script (F11), so its kept plots are the ones it would make
          heights: heights ? qcScriptHeights({ lineName: l.line_name, ...heights }) : [],
        });
      views.set(id, at);
    }
  }
  return [...views.values()];
}

// ------------------------------------------------------------------------------ decision 847: Set the zone

/** One database "Set the zone" would change. */
export interface GeosoftZoneFixDatabase {
  readonly block_folder: string;
  readonly name: string;
  /** Its coordinate system now, `WGS 84 / UTM zone 59S`; null when it has none. */
  readonly from: string | null;
}

/** What "Set the zone" does: the flight plan's zone on each database stamped another or with none. */
export interface GeosoftZoneFix {
  readonly epsg: number;
  /** `WGS 84 / UTM zone 50S`. */
  readonly zone: string;
  readonly databases: readonly GeosoftZoneFixDatabase[];
}

/**
 * The databases stamped with a zone other than the flight plan's (decision 847, F26048: 29 in 59S, the survey is 50S)
 * and those with no coordinate system at all (F26048's boundary databases, `Lines.gdb`), and that zone: one press
 * labels them all (MB, 29 Oct 2026). Databases that were not read are left out (their warning stays). Null when there
 * is nothing to set or the flight plan gives no single zone.
 */
export function geosoftZoneFix(
  expected: number | null,
  geosoft: readonly {
    readonly block_folder: string;
    readonly databases: readonly {
      readonly name: string;
      readonly read: boolean;
      readonly projection: { readonly name: string; readonly epsg: number | null } | null;
    }[];
  }[],
): GeosoftZoneFix | null {
  const zone = expected === null ? null : utmZoneName(expected);
  if (expected === null || zone === null) return null;
  const databases = geosoft.flatMap((g) =>
    g.databases
      .filter((d) => d.read && (d.projection === null || d.projection.epsg !== expected))
      .map((d) => ({ block_folder: g.block_folder, name: d.name, from: d.projection?.name ?? null })),
  );
  return databases.length === 0 ? null : { epsg: expected, zone, databases };
}

/**
 * Decision 939: "Set the zone" by block. `wantOf` gives each block folder's zone (its own lines file); folders with
 * the same zone share one fix, since one press sets one zone. Empty when there is nothing to set.
 */
export function geosoftZoneFixes<G extends Parameters<typeof geosoftZoneFix>[1][number]>(
  wantOf: (folder: G) => number | null,
  geosoft: readonly G[],
): GeosoftZoneFix[] {
  const byZone = new Map<number, G[]>();
  for (const g of geosoft) {
    const want = wantOf(g);
    if (want !== null) byZone.set(want, [...(byZone.get(want) ?? []), g]);
  }
  return [...byZone]
    .sort(([a], [b]) => a - b)
    .map(([want, folders]) => geosoftZoneFix(want, folders))
    .filter((f): f is GeosoftZoneFix => f !== null);
}

/** "29 in WGS 84 / UTM zone 59S and 6 with no coordinate system". */
function zoneFixFroms(fix: GeosoftZoneFix): string {
  const counts = new Map<string, number>();
  for (const d of fix.databases) {
    const k = d.from ?? '';
    counts.set(k, (counts.get(k) ?? 0) + 1);
  }
  const parts = [...counts]
    .sort(([a], [b]) => (a === '' ? 1 : b === '' ? -1 : a.localeCompare(b)))
    .map(([from, n]) => (from === '' ? `${n} with no coordinate system` : `${n} in ${from}`));
  return parts.length <= 1
    ? (parts[0] ?? '')
    : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

/**
 * The confirmation "Set the zone" asks before it changes anything (decision 847: confirm before changing), with the
 * list (`geosoftZoneFixList`) shown under it. A label fix: the X/Y channels' coordinate system is set; no coordinate
 * is converted.
 */
export function geosoftZoneFixQuestion(fix: GeosoftZoneFix): string {
  const n = fix.databases.length;
  const what = n === 1 ? fix.databases[0]!.name : `these ${n} databases`;
  return `Set the coordinate system of ${what} (${zoneFixFroms(fix)}) to ${fix.zone}, the flight plan's zone? Only the label of the X/Y channels is set: no coordinates are converted. Oasis montaj changes the project's own databases in place; close them in Oasis montaj first.`;
}

/** One line per database for the confirmation: `F26048_Milgun/Flt02.gdb: WGS 84 / UTM zone 59S`. */
export function geosoftZoneFixList(fix: GeosoftZoneFix): string[] {
  return fix.databases.map((d) => `${d.block_folder}/${d.name}: ${d.from ?? 'no coordinate system'}`);
}

// ------------------------------------------------------------- decision 868: one flight, several blocks' databases

/** A block folder's Geosoft folder as `geosoft_flight_db_prepare` found it. */
export interface FlightDbBlockFolder {
  /** `F26048_Milgun_SouthEast`. */
  readonly block_folder: string;
  /** It has a Geosoft project (`.gpf`) of its own (decision 868: each block's own project). */
  readonly has_project: boolean;
  /** Where the flight's database would go in it: `…\Geosoft\Database\Flt09.gdb`. */
  readonly target: string;
  /** Its `Geosoft\Scripts` folders, after the template was copied in. */
  readonly folders: readonly { readonly name: string; readonly scripts: readonly string[] }[];
}

/** One database to make for a flight: in which block folder, which lines, which script. */
export interface FlightDbTarget {
  readonly block_folder: string;
  readonly target: string;
  /** The XYZ lines it takes (numbers as the XYZ writes them, `301780`); null: every line (one database). */
  readonly lines: readonly number[] | null;
  /** The aircraft type's script 1 in that block's Scripts folder; null: import only. */
  readonly script: string | null;
}

/** The block folder of a block: `F26048_Milgun_patch` for the key `Milgun_patch` (the folder ends with it). */
export function blockFolderOf(block: Pick<Block, 'key'>, folders: readonly string[]): string | null {
  const key = block.key.toLowerCase();
  return folders.find((f) => f.toLowerCase() === key || f.toLowerCase().endsWith(`_${key}`)) ?? null;
}

/** Test lines, comp-box and calibration lines: numbers below 10000 (`8010`), in every block's database (Q783). */
const isTestLine = (n: number): boolean => n < 10_000;

/**
 * The block a flown line belongs to (decision 879): the first digit of its number is the block number (patches and
 * reflights keep the number of the line they repeat, `301781` is block 3's). Null for a line of no survey block.
 */
export function flightLineBlock<B extends Pick<Block, 'digit' | 'kind'>>(
  number: number,
  blocks: readonly B[],
): B | null {
  const digit = String(number).charAt(0);
  return blocks.find((b) => b.kind === 'survey' && b.digit === digit) ?? null;
}

/**
 * The database of a run that holds a line (decision 879: a flight over several blocks has each block's lines in a
 * database in that block's own folder, checked as one run): the one in the line's block folder, else the run's own
 * (`source_path`; test and calibration lines are in every block's). "Review line" opens it.
 */
export function qcLineGdb(
  run: { readonly source_path: string; readonly spec: Record<string, unknown> },
  lineName: string,
  blocks: readonly Pick<Block, 'key' | 'digit' | 'kind'>[],
): string {
  const more = (run.spec as { more_gdbs?: readonly string[] } | null | undefined)?.more_gdbs ?? [];
  if (more.length === 0) return run.source_path;
  const n = Number(lineName.replace(/^[A-Z]+/, '').split(':')[0]);
  const block = Number.isFinite(n) && !isTestLine(n) ? flightLineBlock(n, blocks) : null;
  if (!block) return run.source_path;
  const all = [run.source_path, ...more];
  // `…\<block folder>\Geosoft\Database\Flt09.gdb`
  const folderOfGdb = (g: string): string => g.split(/[\\/]/).slice(-4, -3)[0] ?? '';
  const folder = blockFolderOf(block, all.map(folderOfGdb));
  return all.find((g) => folder !== null && folderOfGdb(g) === folder) ?? run.source_path;
}

/**
 * Where a flight's lines go (decisions 868 and 879): each block folder with a Geosoft project of its own gets a
 * database of that flight, of the same name, with that block's lines (the first digit) and the flight's test lines; the lines of a block without its own project, or
 * of no block, go to `main` (the folder that holds the project's flight databases, F26048's `F26048_Milgun`). When all
 * the lines land in one folder, that one database takes every line, as before (F26048).
 */
export function flightDbSplit(input: {
  readonly family: QcFamily;
  readonly lineNumbers: readonly number[];
  readonly blocks: readonly Pick<Block, 'key' | 'digit' | 'kind'>[];
  readonly folders: readonly FlightDbBlockFolder[];
  /** The block folder the flight's database goes to when it is one (`database_dir_for`). */
  readonly main: string;
}): FlightDbTarget[] {
  const byFolder = new Map(input.folders.map((f) => [f.block_folder.toLowerCase(), f]));
  const names = input.folders.map((f) => f.block_folder);
  const groups = new Map<string, number[]>();
  const tests: number[] = [];
  for (const n of input.lineNumbers) {
    if (isTestLine(n)) {
      tests.push(n);
      continue;
    }
    const block = flightLineBlock(n, input.blocks);
    const folder = block ? blockFolderOf(block, names) : null;
    const own = folder ? byFolder.get(folder.toLowerCase()) : undefined;
    const to = own?.has_project ? own.block_folder : input.main;
    groups.set(to, [...(groups.get(to) ?? []), n]);
  }
  if (groups.size === 0) groups.set(input.main, []);
  const script = (folder: string): string | null => {
    const f = byFolder.get(folder.toLowerCase());
    const set = f?.folders.find(
      (x) => x.name.toLowerCase() === QC_SCRIPT_FOLDERS[input.family].toLowerCase(),
    );
    return set ? qcProcessingScript(input.family, set.scripts) : null;
  };
  const target = (folder: string): string => byFolder.get(folder.toLowerCase())?.target ?? '';
  if (groups.size === 1) {
    const [only] = [...groups.keys()];
    return [{ block_folder: only!, target: target(only!), lines: null, script: script(only!) }];
  }
  return [...groups].map(([folder, lines]) => ({
    block_folder: folder,
    target: target(folder),
    lines: [...lines, ...tests].sort((a, b) => a - b),
    script: script(folder),
  }));
}

// ------------------------------------------------------------------- decision 953 and comp-box flights (2 Nov 2026)

/**
 * Decision 953 (Q848 B): a flight folder that holds its XYZ but whose log is not imported, as Flight data QC lists it
 * (greyed, with "Import the log"): `Day 305 Flt 001: log not imported yet`.
 */
export function qcUnimportedText(label: string): string {
  return `${label}: log not imported yet`;
}

/** The start of the note Teslyn Field stores on a run whose flight has comp-box lines only (`COMP_BOX_ONLY`). */
export const QC_COMP_BOX_ONLY = 'Comp-box lines only';

/** Whether a run's flight has comp-box lines only (Teslyn Field's note on it). */
export function qcRunCompBoxOnly(run: { readonly notes: readonly string[] }): boolean {
  return run.notes.some((n) => n.startsWith(QC_COMP_BOX_ONLY));
}

/**
 * A run's lines in words (MB, 2 Nov 2026): "comp-box lines only" for a comp-box flight (its lines are all below 10000,
 * Q783), never "0 lines"; else "1 line", "86 lines".
 */
export function qcRunLinesText(run: {
  readonly line_count: number;
  readonly notes: readonly string[];
}): string {
  if (qcRunCompBoxOnly(run)) return 'comp-box lines only';
  return `${run.line_count} ${run.line_count === 1 ? 'line' : 'lines'}`;
}

/**
 * What a flight database Teslyn made holds, in words: `Flt01.gdb: comp-box lines only (L7010, L7011, L7020, L8010)`
 * when it has no survey line, else `Flt09.gdb: 86 lines`.
 */
export function flightDbMadeText(made: {
  readonly gdb: string;
  readonly lines: number;
  readonly comp_box_lines?: readonly string[];
}): string {
  const name = made.gdb.split(/[\\/]/).pop() ?? made.gdb;
  const box = made.comp_box_lines ?? [];
  if (made.lines === 0 && box.length > 0) return `${name}: comp-box lines only (${box.join(', ')})`;
  return `${name}: ${made.lines} ${made.lines === 1 ? 'line' : 'lines'}`;
}
